import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('polling'); // polling | success | failed
  const { refreshUser, api } = useAuth();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('failed');
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('failed');
        }
      } catch {
        setStatus('failed');
      }
    };
    poll();
  }, [sessionId, refreshUser, api]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-sm w-full text-center">
        {status === 'polling' && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 text-primary mx-auto animate-spin" />
            <h2 className="text-2xl font-black text-slate-900 font-fredoka">Processing Payment...</h2>
            <p className="text-slate-500">Hang tight, we're confirming your payment.</p>
          </div>
        )}
        {status === 'success' && (
          <div className="space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-black text-slate-900 font-fredoka">Payment Successful!</h2>
            <p className="text-slate-500">Your weekly subscription is active! Access extended by 7 days.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary mt-4"
              data-testid="go-dashboard-btn"
            >
              Go to Dashboard
            </button>
          </div>
        )}
        {status === 'failed' && (
          <div className="space-y-4">
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h2 className="text-2xl font-black text-slate-900 font-fredoka">Payment Issue</h2>
            <p className="text-slate-500">We couldn't confirm your payment. Please try again or contact support.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-secondary mt-4"
              data-testid="back-dashboard-btn"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
